if (!customElements.get('dio-mode-gallery')) {
  customElements.define(
    'dio-mode-gallery',
    class DioModeGallery extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.tabs = [...this.querySelectorAll('[data-dio-mode-tab]')];
        this.panels = [...this.querySelectorAll('[data-dio-mode-panel]')];
        this.setupStickyRelease();
        if (!this.tabs.length || !this.panels.length) return;

        this.activeIndex = Math.max(0, this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true'));
        this.onClick = this.handleClick.bind(this);
        this.onKeydown = this.handleKeydown.bind(this);
        this.onVisibility = this.handleVisibility.bind(this);
        this.onBlockSelect = this.handleBlockSelect.bind(this);
        this.addEventListener('click', this.onClick);
        this.addEventListener('keydown', this.onKeydown);
        document.addEventListener('visibilitychange', this.onVisibility);
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        this.activate(this.activeIndex, false);

        this.autoplayEnabled = this.dataset.dioAutoplay === 'true' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (this.autoplayEnabled && this.tabs.length > 1) this.observeVisibility();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('keydown', this.onKeydown);
        document.removeEventListener('visibilitychange', this.onVisibility);
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        this.visibilityObserver?.disconnect();
        this.stickyResizeObserver?.disconnect();
        if (this.onStickyFallbackResize) window.removeEventListener('resize', this.onStickyFallbackResize);
        if (this.stickyReleaseFrame) window.cancelAnimationFrame(this.stickyReleaseFrame);
        this.stickyBoundary?.style.removeProperty('--dio-eyebrow-release-space');
        this.stopAutoplay();
        this.initialized = false;
      }

      setupStickyRelease() {
        this.stickyBoundary = this.closest('.dio-hero__sticky-boundary');
        this.stickyControls = this.querySelector('.dio-mode-gallery__controls');
        if (!this.stickyBoundary || !this.stickyControls) return;

        const updateReleaseSpace = () => {
          if (this.stickyReleaseFrame) window.cancelAnimationFrame(this.stickyReleaseFrame);
          this.stickyReleaseFrame = window.requestAnimationFrame(() => {
            if (!this.isConnected) return;
            const boundaryBottom = this.stickyBoundary.getBoundingClientRect().bottom;
            const controlsTop = this.stickyControls.getBoundingClientRect().top;
            const releaseSpace = Math.max(0, Math.ceil(boundaryBottom - controlsTop));
            this.stickyBoundary.style.setProperty('--dio-eyebrow-release-space', `${releaseSpace}px`);
          });
        };

        updateReleaseSpace();
        if ('ResizeObserver' in window) {
          this.stickyResizeObserver = new ResizeObserver(updateReleaseSpace);
          this.stickyResizeObserver.observe(this.stickyBoundary);
          this.stickyResizeObserver.observe(this.stickyControls);
        } else {
          this.onStickyFallbackResize = updateReleaseSpace;
          window.addEventListener('resize', this.onStickyFallbackResize, { passive: true });
        }
      }

      handleClick(event) {
        const tab = event.target.closest('[data-dio-mode-tab]');
        if (!tab || !this.contains(tab)) return;
        this.activate(this.tabs.indexOf(tab), true);
      }

      handleKeydown(event) {
        const currentIndex = this.tabs.indexOf(event.target.closest('[data-dio-mode-tab]'));
        if (currentIndex < 0) return;

        let nextIndex;
        if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.tabs.length;
        if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = this.tabs.length - 1;
        if (nextIndex === undefined) return;

        event.preventDefault();
        this.activate(nextIndex, true);
        this.tabs[nextIndex].focus();
      }

      handleBlockSelect(event) {
        const blockId = String(event.detail?.blockId || event.target.closest?.('[data-dio-block-id]')?.dataset.dioBlockId || '');
        if (!blockId) return;
        const index = this.tabs.findIndex((tab) => tab.dataset.dioBlockId === blockId);
        if (index >= 0) this.activate(index, true);
      }

      activate(index, userInitiated) {
        if (index < 0 || index >= this.tabs.length) return;
        this.activeIndex = index;
        this.tabs.forEach((tab, tabIndex) => {
          const active = tabIndex === index;
          tab.classList.toggle('is-active', active);
          tab.setAttribute('aria-selected', String(active));
          tab.tabIndex = active ? 0 : -1;
        });
        this.panels.forEach((panel, panelIndex) => {
          const active = panelIndex === index;
          panel.classList.toggle('is-active', active);
          panel.setAttribute('aria-hidden', String(!active));
        });
        const accent = this.tabs[index].dataset.dioAccent;
        if (accent) this.style.setProperty('--dio-active-accent', accent);
        this.dispatchEvent(
          new CustomEvent('dio:mode-change', {
            bubbles: true,
            detail: { blockId: this.tabs[index].dataset.dioBlockId, index },
          })
        );
        if (userInitiated) {
          this.autoplayEnabled = false;
          this.stopAutoplay();
        }
      }

      observeVisibility() {
        if (!('IntersectionObserver' in window)) {
          this.inView = true;
          this.startAutoplay();
          return;
        }
        this.visibilityObserver = new IntersectionObserver(([entry]) => {
          this.inView = entry.isIntersecting && entry.intersectionRatio >= 0.3;
          if (this.inView && !document.hidden) this.startAutoplay();
          else this.stopAutoplay();
        }, { threshold: [0, 0.3] });
        this.visibilityObserver.observe(this);
      }

      startAutoplay() {
        if (!this.autoplayEnabled || this.timer) return;
        const interval = Number(this.dataset.dioInterval) || 5000;
        this.timer = window.setInterval(() => this.activate((this.activeIndex + 1) % this.tabs.length, false), interval);
      }

      stopAutoplay() {
        window.clearInterval(this.timer);
        this.timer = null;
      }

      handleVisibility() {
        if (document.hidden) this.stopAutoplay();
        else if (this.autoplayEnabled && this.inView) this.startAutoplay();
      }
    }
  );
}
